import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAllProducts,
  addProduct,
  updateProduct,
  deleteProduct,
} from '../../services/productService';

// Async thunks for product operations

/**
 * Fetch all products from Firestore
 */
export const fetchProducts = createAsyncThunk(
  'product/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const products = await getAllProducts();
      return products;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Create a new product
 */
export const createProduct = createAsyncThunk(
  'product/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const productId = await addProduct(productData);
      return { id: productId, ...productData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update an existing product
 */
export const updateProductById = createAsyncThunk(
  'product/updateProduct',
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      await updateProduct(productId, productData);
      return { id: productId, ...productData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Delete a product
 */
export const deleteProductById = createAsyncThunk(
  'product/deleteProduct',
  async (productId, { rejectWithValue }) => {
    try {
      await deleteProduct(productId);
      return productId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update product status (active/disabled)
 */
export const updateProductStatus = createAsyncThunk(
  'product/updateProductStatus',
  async ({ productId, isActive }, { rejectWithValue }) => {
    try {
      await updateProduct(productId, { isActive });
      return { id: productId, isActive };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  products: [],
  loading: false,
  error: null,
  success: false,
};

// Product slice
const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    // Clear error and success messages
    clearMessages: (state) => {
      state.error = null;
      state.success = false;
    },
    // Reset product state
    resetProductState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch products';
      })

      // Create product
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products.unshift(action.payload);
        state.success = true;
        state.error = null;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create product';
        state.success = false;
      })

      // Update product
      .addCase(updateProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateProductById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.products.findIndex((prod) => prod.id === action.payload.id);
        if (index !== -1) {
          state.products[index] = { ...state.products[index], ...action.payload };
        }
        state.success = true;
        state.error = null;
      })
      .addCase(updateProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update product';
        state.success = false;
      })

      // Delete product
      .addCase(deleteProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.products = state.products.filter((prod) => prod.id !== action.payload);
        state.success = true;
        state.error = null;
      })
      .addCase(deleteProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete product';
        state.success = false;
      })

      // Update product status
      .addCase(updateProductStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateProductStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.products.findIndex((prod) => prod.id === action.payload.id);
        if (index !== -1) {
          state.products[index].isActive = action.payload.isActive;
        }
        state.success = true;
        state.error = null;
      })
      .addCase(updateProductStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update product status';
        state.success = false;
      });
  },
});

export const { clearMessages, resetProductState } = productSlice.actions;
export default productSlice.reducer;
